<template>
  <PageSection font="medium">
    <SectionGroup gap="medium">
      <SectionBox>
        <RatingsPageHeader
          :period.sync="state.period"
          :period-options="periodOptions"
          :tab.sync="state.tab"
          :tab-options="tabOptions"
          :rate-href="rateHref"
          :help-href="helpHref"
          @update:period="scheduleListUpdate"
          @update:tab="scheduleListUpdate"
        />

        <RatingsPageSearchPanel>
          <SearchInput
            v-model="state.query"
            @update:modelValue="scheduleListUpdate"
            type="search"
            placeholder="Поиск"
          />
          <BasicSelect
            v-model="state.order"
            @update:modelValue="scheduleListUpdate"
            :options="orderOptions"
          />
        </RatingsPageSearchPanel>

        <BasicSection appearance="search-panel">
          <TransitionExpand>
            <SearchTotal
              v-if="searchResults.list.length !== 0"
              v-model="searchResultsTitle"
              appearance="search-panel"
            />
          </TransitionExpand>
        </BasicSection>
      </SectionBox>
      <PaginatedResource
        ref="resource"
        v-model="searchResults"
        :action="action"
        :params="params"
        :debounce="400"
        @success="onListRequestSuccess"
        v-slot="{ isLoading }"
      >
        <TransitionExpand>
          <WithPagination
            :params="mappedParams"
            @click="onPagination"
            :key="isLoading"
            ref="scrollTarget"
          >
            <template v-if="isLoading">
              <SectionBox
                v-for="(_, index) in Array(searchResults.list.length < 3 ? 3 : searchResults.list.length)"
                :key="index"
              >
                <BasicSection appearance="horizontal-card">
                  <RatingCardSkeleton />
                </BasicSection>
              </SectionBox>
            </template>

            <template v-if="!isLoading">
              <template v-if="searchResults.list.length !== 0">
                <SectionBox
                  v-for="ratingItem in searchResults.list"
                  :key="ratingItem.id"
                >
                  <BasicSection appearance="horizontal-card">
                    <RatingCard :rating="ratingItem" />
                  </BasicSection>
                </SectionBox>
              </template>

              <SectionBox v-if="searchResults.list.length === 0">
                <BasicSection>
                  <NoContent
                    :title="$options.noContent.title"
                    :description="$options.noContent.description"
                    :icon="$options.noContent.icon"
                    :actions="$options.noContent.actions"
                  />
                </BasicSection>
              </SectionBox>
            </template>
          </WithPagination>
        </TransitionExpand>
      </PaginatedResource>
    </SectionGroup>
    <VuePortal target="[data-subscribe]">
      <PageSection>
        <NewsSubscribe :show-subscribe="showSubscribe"/>
      </PageSection>
    </VuePortal>
  </PageSection>
</template>

<script>
  import { RatingsService } from 'src/services/ratings.js';
  import { getFormattedNumber, getRussianPluralDeclension } from 'src/utils/translation.js';

  import BasicSelect from 'src/components/form/BasicSelect/BasicSelect.vue';
  import SearchInput from 'src/components/form/SearchInput/SearchInput.vue';
  import NewsSubscribe from 'src/components/NewsSubscribe/NewsSubscribe.vue';
  import PaginatedResource from 'src/components/pagination/PaginatedResource/PaginatedResource.vue';
  import WithPagination from 'src/components/pagination/WithPagination/WithPagination.vue';
  import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
  import RatingCard from 'src/components/ratings/RatingCard/RatingCard.vue';
  import RatingCardSkeleton from 'src/components/ratings/RatingCardSkeleton/RatingCardSkeleton.vue';
  import RatingsPageHeader from 'src/components/ratings/RatingsPageHeader/RatingsPageHeader.vue';
  import RatingsPageSearchPanel from 'src/components/ratings/RatingsPageSearchPanel/RatingsPageSearchPanel.vue';
  import SearchTotal from 'src/components/SearchTotal/SearchTotal.vue';
  import SectionBox from 'src/components/SectionBox/SectionBox.vue';
  import BasicSection from 'src/components/sections/BasicSection/BasicSection.vue';
  import PageSection from 'src/components/sections/PageSection/PageSection.vue';
  import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';
  import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';
  import VuePortal from 'src/components/VuePortal/VuePortal.vue';

  import noContentIconSrc from 'images/no_content.icon.companies.png';
  
  const searchResultsDeclension = getRussianPluralDeclension(
    '${0} компания',
    '${0} компании',
    '${0} компаний'
  );
  
  export default {
    name: 'RatingsPageBody',
    components: {
      RatingCardSkeleton,
      RatingsPageSearchPanel,
      RatingsPageHeader,
      BasicSection,
      TransitionExpand,
      PaginatedResource,
      WithPagination,
      SearchTotal,
      BasicSelect,
      SearchInput,
      SectionBox,
      SectionGroup,
      RatingCard,
      NoContent,
      PageSection,
      NewsSubscribe,
      VuePortal,
    },
    noContent: {
      title: 'Компании не найдены',
      description: 'Попробуйте изменить параметры поиска',
      icon: {
        src: noContentIconSrc,
        width: '110px',
        height: '89px',
      },
    },
    props: {
      ratings: {
        type: Object,
        required: true,
      },
      query: String,
      order: {
        type: String,
        required: true,
      },
      orderOptions: {
        type: Array,
        required: true,
      },
      tab: {
        type:  [String, Number],
        required: true,
      },
      tabOptions: {
        type: Array,
        required: true,
      },
      period: {
        type: String,
        required: true,
      },
      periodOptions: {
        type: Array,
        required: true,
      },
      rateHref: String,
      helpHref: {
        type: String,
        required: true,
      },
    },
    data() {
      const {
        tab,
        period,
        query,
        order,
        ratings: searchResults,
      } = this;
      
      return {
        searchResults,
        state: {
          query,
          order,
          tab,
          period,
        }
      }
    },
    methods: {
      action: RatingsService.getList,
      scheduleListUpdate() {
        this.$nextTick(() => {
          this.$refs.resource.loadAndReplace();
        });
      },
      onListRequestSuccess({ params }) {
        RatingsService.syncLocation(params);
      },
      onPagination() {
        this.$refs.scrollTarget.$el.scrollIntoView({
          block: 'start',
          inline: 'nearest',
          behavior: 'auto'
        });
      },
      showSubscribe({ hasSubscribe }) {
        return !hasSubscribe;
      },
    },
    computed: {
      params() {
        const { query, order, period, tab: companySize } = this.state;
        return {
          query,
          order,
          period,
          companySize,
        };
      },
      searchResultsTitle() {
        const { totalResults, counterDescription } = this.searchResults.meta;
        const value = getFormattedNumber(totalResults);
        let result = searchResultsDeclension(value).replace('${0}', value);
        if (counterDescription) result += `, ${counterDescription}`;
        return result;
      },
      mappedParams() {
        return RatingsService.getMappedParams(this.params);
      },
    }
  }
</script>
